/**
 * UserDetailsModal Component
 *
 * Modal for viewing user details in a read-only format.
 */

import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { CopyableCode } from '../../../components/ui/CopyableCode';
import { UserStatusBadge } from './UserStatusBadge';
import { useAuth } from '../../../contexts/AuthContext';
import { useGroups } from '../../groups/hooks/useGroups';
import type { UserSanitized } from '../../../types/user.types';
import { formatDate } from '../../../lib/utils/format';

interface UserDetailsModalProps {
  user: UserSanitized | null;
  isOpen: boolean;
  onClose: () => void;
}

export const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  user,
  isOpen,
  onClose,
}) => {
  const { tenant } = useAuth();
  const tenantId = tenant?.tenantId || '';

  // Fetch groups to display user's group memberships
  const { data: allGroups = [] } = useGroups(tenantId);

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>User Details</Modal.Header>

      <Modal.Body>
        <div className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Basic Information
            </h3>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Full Name</dt>
                <dd className="mt-1 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-medium text-sm">
                    {user.firstName[0]}{user.lastName[0]}
                  </div>
                  <span className="text-sm text-gray-900">
                    {user.firstName} {user.lastName}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Email Address</dt>
                <dd className="mt-1 text-sm text-gray-900">{user.email}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">User ID</dt>
                <dd className="mt-1">
                  <CopyableCode value={user.userId} />
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1">
                  <UserStatusBadge status={user.status as 'active' | 'inactive' | 'suspended' | 'pending'} />
                </dd>
              </div>
            </dl>
          </div>

          {/* Permissions */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Permissions</h3>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Admin Status</dt>
                <dd className="mt-1">
                  {user.isAdmin ? (
                    <Badge variant="warning">Admin</Badge>
                  ) : (
                    <span className="text-sm text-gray-500">Standard User</span>
                  )}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Group Memberships</dt>
                <dd className="mt-1">
                  {user.groups && user.groups.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {user.groups.map((groupAssignment) => {
                        const group = allGroups.find((g) => g.groupId === groupAssignment.groupId);
                        return (
                          <Badge key={groupAssignment.groupId} variant="info">
                            {group ? group.groupName : groupAssignment.groupCode || `${groupAssignment.groupId.slice(0, 8)}...`}
                          </Badge>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500 italic">Not a member of any groups</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>

          {/* Metadata */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Metadata</h3>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Created At</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(user.createdAt, 'MMM dd, yyyy HH:mm')}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Updated At</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(user.updatedAt, 'MMM dd, yyyy HH:mm')}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
